import dayjs from "dayjs";

/**
 * Calculates the optimal domain maximum and tick values for chart axes.
 *
 * This function determines appropriate axis scaling and tick intervals for charts
 * by analyzing the maximum value in the dataset. It ensures visually pleasing
 * tick spacing and handles edge cases like zero or very small values.
 *
 * @param numericValues - Array of numeric values from the dataset
 * @param maxValue - The maximum value in the dataset
 * @returns Object containing axisDomainMax (the calculated maximum for the axis domain)
 *          and axisTicks (array of tick values for the axis)
 *
 * @example
 * ```typescript
 * const result = domainTickCalculate([1, 5, 10], 10);
 * // Returns: { axisDomainMax: 12, axisTicks: [0, 2, 4, 6, 8, 10, 12] }
 * ```
 */
export function domainTickCalculate(numericValues: number[], maxValue: number) {
  let axisDomainMax;
  let axisTicks;

  if (
    maxValue === 0 &&
    (numericValues.length === 0 || numericValues.every((v) => v === 0))
  ) {
    axisDomainMax = 10;
    axisTicks = [0, 2, 4, 6, 8, 10];
  } else {
    let preliminaryDomainMax = Math.ceil(maxValue * 1.15);
    if (maxValue > 0 && preliminaryDomainMax <= maxValue) {
      preliminaryDomainMax = maxValue + Math.max(1, Math.ceil(maxValue * 0.05));
    }
    if (maxValue > 0 && preliminaryDomainMax === 0) {
      preliminaryDomainMax = Math.max(1, Math.ceil(maxValue * 1.15));
    }
    if (preliminaryDomainMax === 0) {
      preliminaryDomainMax = 10;
    }

    const targetTickCount = Math.min(
      Math.max(5, Math.floor(preliminaryDomainMax / 2) + 1),
      8
    );

    let niceInterval;
    if (preliminaryDomainMax <= 1) {
      niceInterval = 0.2;
    } else if (preliminaryDomainMax <= 5) {
      niceInterval = 1;
    } else if (preliminaryDomainMax <= 10) {
      niceInterval = 2;
    } else {
      const rawInterval =
        preliminaryDomainMax / (targetTickCount > 1 ? targetTickCount - 1 : 1);
      const exponent = Math.floor(Math.log10(rawInterval));
      const powerOf10 = Math.pow(10, exponent);
      const significand = rawInterval / powerOf10;

      if (significand <= 1) niceInterval = powerOf10;
      else if (significand <= 2) niceInterval = 2 * powerOf10;
      else if (significand <= 5) niceInterval = 5 * powerOf10;
      else niceInterval = 10 * powerOf10;
    }

    if (
      niceInterval === 0 ||
      Number.isNaN(niceInterval) ||
      niceInterval < 0.00001
    ) {
      niceInterval = 1;
    }

    axisDomainMax =
      Math.ceil(preliminaryDomainMax / niceInterval) * niceInterval;
    if (
      Number.isNaN(axisDomainMax) ||
      (axisDomainMax < preliminaryDomainMax && preliminaryDomainMax > 0)
    ) {
      axisDomainMax = preliminaryDomainMax;
    }
    if (maxValue > 0 && axisDomainMax === 0) {
      axisDomainMax = niceInterval;
    }

    axisTicks = [];
    const precisionFactor =
      niceInterval < 1
        ? Math.pow(10, String(niceInterval).split(".")[1]?.length || 2)
        : 1;
    for (let i = 0; ; i++) {
      const tickValue =
        Math.round(i * niceInterval * precisionFactor) / precisionFactor;
      if (tickValue > axisDomainMax + niceInterval / 2) break;
      axisTicks.push(tickValue);
      if (tickValue >= axisDomainMax && axisTicks.length > 1) break;
      if (i > 200) break;
    }

    if (
      axisTicks.length > 0 &&
      axisTicks[axisTicks.length - 1] < axisDomainMax - niceInterval / 2 &&
      axisDomainMax > 0
    ) {
      if (!axisTicks.includes(axisDomainMax)) {
        axisTicks.push(axisDomainMax);
        axisTicks.sort((a, b) => a - b);
      }
    }

    if (axisTicks.length > 0 && axisTicks[axisTicks.length - 1] < maxValue) {
      axisDomainMax = Math.ceil(maxValue / niceInterval) * niceInterval;
      if (!axisTicks.includes(axisDomainMax)) axisTicks.push(axisDomainMax);
      axisTicks = [...new Set(axisTicks)].sort((a, b) => a - b);
    }

    if (axisTicks.length === 0 && axisDomainMax > 0) {
      axisTicks = [0, axisDomainMax];
    } else if (
      axisTicks.length === 1 &&
      axisTicks[0] === 0 &&
      axisDomainMax > 0
    ) {
      axisTicks.push(axisDomainMax);
    } else if (axisTicks.length === 0 && axisDomainMax === 0) {
      axisDomainMax = 10;
      axisTicks = [0, 2, 4, 6, 8, 10];
    }
  }

  axisTicks = [...new Set(axisTicks)].sort((a, b) => a - b);
  if (axisTicks.length === 1 && axisDomainMax > axisTicks[0]) {
    axisTicks.push(axisDomainMax);
    axisTicks.sort((a, b) => a - b);
  }
  if (axisTicks.length === 0) axisTicks = [0, 10];
  if (axisDomainMax === undefined || Number.isNaN(axisDomainMax)) {
    axisDomainMax = Math.max(...axisTicks, 10);
  }

  return { axisDomainMax, axisTicks };
}

/**
 * Calculates evenly distributed tick values for date-based axes.
 *
 * This function analyzes the date range in the dataset and generates an appropriate
 * number of tick marks that are evenly spaced across the timeline. It converts dates
 * to timestamps for proper numerical spacing.
 *
 * @param data - Array of data objects containing date values
 * @param dateKey - The key in the data objects that contains the date value
 * @param maxTicks - Maximum number of ticks to generate (default: 5)
 * @returns Array of timestamp values to use as axis ticks
 *
 * @example
 * ```typescript
 * const data = [
 *   { date: '2024-01-01', value: 10 },
 *   { date: '2024-01-15', value: 20 }
 * ];
 * const ticks = calculateDateTicks(data, 'date', 5);
 * // Returns: [timestamp1, timestamp2, timestamp3, timestamp4, timestamp5]
 * ```
 */
export const calculateDateTicks = (
  data: Record<string, unknown>[],
  dateKey: string,
  maxTicks: number = 5
): number[] => {
  if (!data.length) return [];

  // Convert all dates to timestamps
  const timestamps = data
    .map((item) => {
      const date = dayjs(item[dateKey] as string | number | Date);
      return date.isValid() ? date.valueOf() : Number.NaN;
    })
    .filter((ts) => !Number.isNaN(ts))
    .sort((a, b) => a - b);

  if (timestamps.length === 0) return [];

  const min = timestamps[0];
  const max = timestamps[timestamps.length - 1];
  const range = max - min;

  // If all dates are the same, return just that date
  if (range === 0) return [min];

  // Calculate interval based on range
  const interval = range / (maxTicks - 1);

  // Generate evenly spaced ticks
  const ticks: number[] = [];
  for (let i = 0; i < maxTicks; i++) {
    ticks.push(min + interval * i);
  }

  return ticks;
};

/**
 * Formats a timestamp into a human-readable date string.
 *
 * The format automatically adjusts based on the date range to provide
 * the most appropriate level of detail:
 * - Very short ranges (≤7 days): Shows month, day, and hour
 * - Short ranges (≤60 days): Shows month and day
 * - Medium ranges (≤365 days): Shows month and year
 * - Long ranges (>365 days): Shows year only
 *
 * @param timestamp - Unix timestamp in milliseconds
 * @param range - Optional range in milliseconds to determine formatting granularity
 * @returns Formatted date string
 *
 * @example
 * ```typescript
 * const timestamp = new Date('2024-01-15').getTime();
 * formatDateTick(timestamp); // "Jan 15"
 * formatDateTick(timestamp, 7 * 24 * 60 * 60 * 1000); // "Jan 15, 12 AM" (week range)
 * ```
 */
export const formatDateTick = (timestamp: number, range?: number): string => {
  const date = dayjs(timestamp);

  if (!range) {
    return date.format("MMM D");
  }

  // Adjust format based on date range
  const daysDiff = range / (1000 * 60 * 60 * 24);

  if (daysDiff <= 7) {
    // Less than a week: show day and time
    return date.format("MMM D, hA");
  } else if (daysDiff <= 60) {
    // Less than 2 months: show month and day
    return date.format("MMM D");
  } else if (daysDiff <= 365) {
    // Less than a year: show month and year
    return date.format("MMM YYYY");
  } else {
    // More than a year: show year only
    return date.format("YYYY");
  }
};
